/**
 * @file SelectComponentCustom.tsx
 * @description
 * -----------------------------------------------------
 * Componente de selección personalizado
 * -----------------------------------------------------
 * @version 1.0.0a
 * @created 2025-07-21
 * @modified 2026-02-03
 * @author Correcta
 * @author Cristina Bosa
 * @author Imane Benabdellah
 * @author Pedro Luis Gómez
 * @modified Sergio Manjón
 * @copyright © Correcta
 * @license Private
 * */

import { useEffect, useId, useRef, useState } from "react";
import { useTranslations } from "use-intl";

import ButtonComponent from "@/components/ui/ButtonComponent";
import FloatingTooltip from "@/components/ui/FloatingTooltip";

import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";

import { IconType } from "@/types/ui";
import GenericTooltip from "@/components/ui/GenericTooltip";
import {
  DEFAULT_TOOLTIP_TRIGGER_LENGTH,
  SELECT_TOOLTIP_MAX_CONTENT_LENGTH,
} from "@/constants/tooltip";
import { getTooltipText } from "@/lib/text";

export interface Option {
  readonly value?: string | number;
  readonly label: string;
  className?: string;
  icon?: IconType;
}

interface SelectComponentCustomProps {
  readonly id?: string;
  readonly name?: string;
  readonly label?: string;
  readonly value: string | number | undefined;
  readonly options: Option[];
  readonly placeholder?: string;
  onChange?: (_e: React.ChangeEvent<HTMLSelectElement>) => void;
  error?: string;
  needLabel?: boolean;
  noTranslation?: boolean;
  className?: string;
  // Nuevas props para modo display (MultiCombobox)
  displayMode?: boolean;
  displayText?: string;
  onDisplayClick?: () => void;
  isExpanded?: boolean;
  badgeClassName?: string;
  ariaLabel?: string;
  onSelect?: (_value: string) => void;
  selectedValue?: string | number;
  isRequired?: boolean;
  disabled?: boolean;
  useDefaultPlaceholder?: boolean;
  placeholderValue?: string;
  tooltip?: {
    title?: string;
    content: React.ReactNode;
  };
}

const SelectComponent = ({
  id,
  name,
  label,
  value,
  options,
  placeholder,
  onChange,
  onSelect,
  selectedValue,
  error,
  ariaLabel,
  needLabel = true,
  noTranslation = false,
  isRequired = false,
  className = "",
  disabled = false,
  useDefaultPlaceholder = true,
  tooltip,
  placeholderValue = "",
}: SelectComponentCustomProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const optionsTranslation = useTranslations("SelectComponent.Options");
  const errors = useTranslations("Validation");

  const currentValue = value !== undefined ? value : selectedValue;

  const isDisabled = disabled || options.length === 1;

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleDropdownClick = () => {
    if (!isDisabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleSelect = (selectedValue: string | number | undefined) => {
    if (selectedValue === undefined) return;

    const normalizedValue = selectedValue === "" ? "" : selectedValue;

    if (onChange) {
      const syntheticEvent = {
        target: {
          id: id || name || "",
          name: name || id || "",
          value: normalizedValue,
        },
      } as React.ChangeEvent<HTMLSelectElement>;

      onChange(syntheticEvent);
    }

    if (onSelect) {
      onSelect(String(normalizedValue));
    }

    setIsOpen(false);
  };

  const normalizedCurrentValue =
    currentValue === undefined
      ? String(placeholderValue)
      : String(currentValue);

  const isPlaceholderSelected =
    normalizedCurrentValue === String(placeholderValue);

  const selectedOption = options.find((opt: Option) => {
    const optValue = opt.value === undefined ? "" : String(opt.value);
    return optValue === normalizedCurrentValue;
  });

  const selectedLabel = selectedOption?.label;
  const SelectedIcon = selectedOption?.icon;
  const defaultPlaceholder = useDefaultPlaceholder
    ? optionsTranslation("select")
    : "";
  const selectedDisplayLabel = noTranslation
    ? selectedLabel && selectedLabel !== "select"
      ? selectedLabel
      : placeholder || defaultPlaceholder
    : selectedLabel
      ? optionsTranslation(selectedLabel)
      : placeholder || defaultPlaceholder;
  const selectedTooltip = getTooltipText(
    selectedDisplayLabel,
    DEFAULT_TOOLTIP_TRIGGER_LENGTH,
    SELECT_TOOLTIP_MAX_CONTENT_LENGTH,
  );

  return (
    <article className={`input__group ${className}`}>
      {/* Label opcional */}
      {needLabel && label && (
        <label className={`label ${error ? "label--error" : ""}`} htmlFor={id}>
          {label} {isRequired && "*"}
          {tooltip && (
            <GenericTooltip title={tooltip.title}>
              {tooltip.content}
            </GenericTooltip>
          )}
        </label>
      )}

      <input type="hidden" name={name || id} value={currentValue || ""} />

      {/* Contenedor del select personalizado */}
      <section
        className={`select-group__wrapper ${className}`}
        ref={dropdownRef}
        onBlurCapture={(event) => {
          const nextFocus = event.relatedTarget as Node | null;
          if (!nextFocus || !event.currentTarget.contains(nextFocus)) {
            setIsOpen(false);
          }
        }}
      >
        <ButtonComponent
          id={id}
          type="button"
          status="select"
          onClick={handleDropdownClick}
          disabled={isDisabled}
          ariaLabel={ariaLabel || label}
          ariaExpanded={isOpen}
          ariaControls={listboxId}
          ariaHasPopup="listbox"
          className={`select ${isOpen ? "select__open" : ""} ${className} ${error ? "select__error" : ""} ${isDisabled ? "select__disabled" : ""}`}
        >
          {SelectedIcon && <SelectedIcon aria-hidden="true" size={24} />}
          <FloatingTooltip
            content={selectedTooltip}
            side="bottom"
            className="floating-tooltip__select"
            disabled={!selectedTooltip}
          >
            <p>{selectedDisplayLabel}</p>
          </FloatingTooltip>
          {options.length > 0 &&
            (isOpen ? (
              <ChevronUpIcon
                className="input-group__icon input-group__icon--right"
                size={12}
              />
            ) : (
              <ChevronDownIcon
                className="input-group__icon input-group__icon--right"
                size={12}
              />
            ))}
        </ButtonComponent>

        {options && options.length > 0 && isOpen && (
          <section className="select__dropdown">
            <ul
              id={listboxId}
              className="select__dropdown__list"
              role="listbox"
            >
              {options
                .filter((option: Option) => {
                  const optValue =
                    option.value === undefined || option.value === ""
                      ? ""
                      : String(option.value);
                  const currValue = normalizedCurrentValue;

                  if (
                    optValue === String(placeholderValue) &&
                    isPlaceholderSelected
                  ) {
                    return false;
                  }

                  return optValue !== currValue;
                })
                .map((option: Option, index: number) => {
                  const keyValue =
                    option.value !== undefined
                      ? String(option.value)
                      : `option-${index}`;
                  const optionDisplayLabel = noTranslation
                    ? option.label !== "select"
                      ? option.label || optionsTranslation("select")
                      : optionsTranslation("select")
                    : optionsTranslation(option.label);
                  const optionTooltip = getTooltipText(
                    optionDisplayLabel,
                    DEFAULT_TOOLTIP_TRIGGER_LENGTH,
                    SELECT_TOOLTIP_MAX_CONTENT_LENGTH,
                  );
                  return (
                    <li
                      key={keyValue}
                      className={`select__dropdown__list--item ${option.className || ""}`}
                      onClick={() => handleSelect(option.value)}
                      role="option"
                      aria-selected={false}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSelect(option.value);
                        }
                      }}
                    >
                      {option.icon && (
                        <option.icon aria-hidden="true" size={16} />
                      )}
                      <FloatingTooltip
                        content={optionTooltip}
                        side="right"
                        className="floating-tooltip__select-option"
                        disabled={!optionTooltip}
                      >
                        <span className="select__option-text">
                          {optionDisplayLabel}
                        </span>
                      </FloatingTooltip>
                    </li>
                  );
                })}
            </ul>
          </section>
        )}
      </section>

      {/* Mensaje de error */}
      {error && (
        <span className="input-group__error-message">{errors(error)}</span>
      )}
    </article>
  );
};

export default SelectComponent;
